<template>
    <AnalysisPane
      :community="community"
      :project="project"
      :get_kpi_fn="fetchInfluenceKPIs"
    >
        <template v-if="kpis">
            <v-row class="project-cards__row row">
                <v-col class="col-lg-6">
                    <ProjectCard title="Distortion coefficients" hint>

                        <template v-slot:tooltip>
                            <p>The distortion coefficient measures how abnormal a community
                                is, compared to the French Panel.</p>

                            <p>Distortion coefficient is a number above or equal to 1. If it
                                is equal to 1, then the community is identical to the French Panel
                                on all our criteria to detect suspicious activity. The higher it
                                is, the more suspicious the community is.</p>

                            <p>
                                The distortion coefficient is made up of six indexes. The first
                                is related to the average chattiness. The second is related to
                                the 10th decile chattiness. The third is related to the recency
                                of accounts. The fourth is related to sleep time. The fifth is
                                related to the number of friends. The sixth is related to the
                                number of followers.
                            </p>
                        </template>

                        <v-row align="center" justify="center">
                            <Plotly :data="[
                                {
                                  domain: { x: [0, 1], y: [0, 1] },
                                  value: round(kpis.propagandaCoefficient.coeff),
                                  title: { text: 'Overall distortion coefficient' },
                                  type: 'indicator',
                                  mode: 'gauge+number',
                                  gauge: {
                                    axis: { range: [1, 10] }
                                  },
                                }
                              ]" :layout="{width: 300, height: 300}"></Plotly>
                        </v-row>

                        <Plotly :data="[kpis.propagandaCoefficient.radar]"
                                :layout="{ polar: { radialaxis: { visible: true,
                                                                  range: [0, 10] } },
                               showlegend: false }"/>
                        <div>
                            <span>Overall coefficient: {{ round(kpis.propagandaCoefficient.coeff) }}</span>
                        </div>

                        <template v-slot:hint>
              <span v-if="kpis.propagandaCoefficient.coeff <= 1.5">
                The community is not suspicious.
              </span>
                            <span v-else-if="kpis.propagandaCoefficient.coeff <= 3">
                The community is slightly suspicious.
              </span>
                            <span v-else>
                The community is highly suspicious.
              </span>
                        </template>
                    </ProjectCard>
                </v-col>
                <v-col class="col-lg-6">
                    <ProjectCard
                            title="Potential reach"
                            subtitle="Median number of followers per user"
                            hint>
                        <v-row class="row">
                            <v-col class="col-lg-6">
                                <div class="text-center">{{ kpis.potentialReach.Community }}</div>
                                <div class="">
                                    <span v-for="i in stickman(kpis.potentialReach.Community)" :key="i">
                                        <span v-if="i < 100">
                                            <img class="human-icon" :src="require('@/assets/img/icons/human.svg')"/>
                                        </span>
                                        <span v-else>+</span>
                                    </span>
                                </div>
                                <div class="text-center">
                                    Community
                                </div>
                            </v-col>
                            <v-col class="col-lg-6">
                                <div class="text-center">{{ kpis.potentialReach.Panel }}</div>
                                <div>
                                    <span v-for="i in stickman(kpis.potentialReach.Panel)" :key="i">
                                        <span v-if="i < 100">
                                            <img class="human-icon--panel" :src="require('@/assets/img/icons/human.svg')"/>
                                        </span>
                                        <span v-else>+</span>
                                    </span>
                                </div>
                                <div class="text-center">Panel</div>
                            </v-col>
                        </v-row>

                        <template v-slot:hint>
                            Half of members of the community have less than {{
                            kpis.potentialReach.Community }} followers.
                        </template>
                    </ProjectCard>
                </v-col>
            </v-row>

            <v-row class="project-cards__row row">
                <v-col class="col-lg-6">
                    <ProjectCard title="Followers by decile group"
                                 hint>
                        <template v-slot:tooltip>
                            After ranking members from the community by their number of
                            followers, we divided the community in ten groups of equal size
                            and show the average number of followers per member for each
                            group.
                        </template>
                        <Plotly :data="deciles(kpis.followerByDecile)"
                                :layout="{xaxis: {'type': followersXScale}, showlegend: false, hovermode: 'closest',
                                          height: 300}"/>
                        <v-btn @click="followersXScale = followersXScale === 'linear' ? 'log' : 'linear'" text small> Switch to {{ followersXScale === 'linear' ? 'Log' : 'Linear' }} scale</v-btn>

                        <template v-slot:hint>
                            The 10% most followed members of the group have
                            not less than {{ Math.floor(Math.max(...kpis.followerByDecile.Community)) }} followers.
                        </template>
                    </ProjectCard>
                </v-col>
                <v-col class="col-lg-6">
                    <ProjectCard
                            title="Curiosity distribution"
                            subtitle="% of members by number of subscriptions"
                            hint>
                        <template v-slot:tooltip>
                            A curious member is a member who has subscribed to a lot of
                            accounts. In the following graph, we show the distribution of the
                            community by number of subscriptions.
                        </template>
                        <Plotly :data="hplotlify(kpis.curiosityDistribution)" :layout="{hovermode: 'closest'}"/>
                        <template v-slot:hint>
                            {{ hintForCuriosity(kpis.curiosityDistribution) }}
                        </template>
                    </ProjectCard>
                </v-col>
            </v-row>

            <v-row class="project-cards__row row">
                <v-col class="col-lg-6">
                    <ProjectCard
                            title="Pro curiosity index"
                            subtitle="% of members who follow at least 3 accounts in a category"
                            hint>
                        <template v-slot:tooltip>
                            Share of members from the community who follow at least three
                            accounts from Lucy Pro Communities.
                        </template>
                        <Plotly :data="hplotlify(proCuriosityAdapter(kpis.proCuriosityIndex))" :layout="{hovermode: 'closest'}"/>
                        <template v-slot:hint>
                            {{ hintProCuriosity(kpis.proCuriosityIndex) }}
                        </template>
                    </ProjectCard>
                </v-col>
            </v-row>

            <v-row class="project-cards__row row">
                <v-col v-for="podium in orderPodiums(kpis.podiums)" :key="podium.name" class="col-lg-6">
                    <ProjectCard
                            :title="'Influence Podium: ' + podium.name"
                            :subtitle="podiumSubtitle(podium)"
                            hint>
                        <template v-slot:tooltip>
                            <div v-if="podium.data.type == 'followers'">
                                Top five accounts from Lucy {{ podium.name }} by number of
                                followers among the community.
                            </div>
                            <div v-else-if="podium.data.type == 'topics'">
                                Top five social topics by the number of
                                members of the community.
                            </div>
                            <div v-else-if="podium.data.type == 'all-followers'">
                                Top five accounts by number of
                                followers among the community.
                            </div>
                            <div v-else-if="podium.data.type == 'followers-other'">
                              Top five accounts that are not part of any of podium communities,
                              by number of followers among the community.
                            </div>
                            <div v-else>
                            </div>
                        </template>
                        <Plotly  v-if="podium.data.Community.length > 0"
                                 :data="plotlify(podium.data)" :layout="{hovermode: 'closest'}"/>
                        <span v-else>
                            <div v-if="podium.data.type == 'followers'">
                                Members of the community do not follow {{ podium.name }} accounts.
                            </div>
                            <div v-else-if="podium.data.type == 'topics'">
                                Member of the community don't tweet about social topics.
                            </div>
                            <div v-else>
                              Members of community do not follow anyone.
                            </div>
                        </span>
                        <template v-slot:hint>
                            {{ hintPodium(podium) }}
                        </template>
                    </ProjectCard>
                </v-col>
            </v-row>
          </template>
    </AnalysisPane>
</template>

<script>
    import _ from "lodash";
    import ProjectCard from "./../ProjectCard";
    import CommunityService from "../../../services/community.service";
    import {VueSvgGauge} from 'vue-svg-gauge'
    import {Plotly} from 'vue-plotly'
    import AnalysisPane from "./AnalysisPane"

    export default {
        name: "Influence",
        components: {ProjectCard, VueSvgGauge, Plotly, AnalysisPane},
        props: ["community", "project"],
        data: () => ({
            followersXScale: "linear",
            kpis: null,
            layout: {
                hovermode: 'closest'
            }
        }),
        methods: {
            stickman(c) {
                let k = _.min([Math.floor(c / 10), 101])
                return _.range(k)
            },
            _plotlify(d, name) {
                return {
                    x: _.map(d, e => e.x),
                    y: _.map(d, e => e.y),
                    type: "bar",
                    name: name,
                    marker: {color: name === 'Community' ? '#0499fe' : '#e3e3e3'},
                    hovertemplate: "%{y:.2f}%"
                }
            },
            _hplotlify(d, name) {
                return {
                    y: _.map(d, e => e.x),
                    x: _.map(d, e => e.y),
                    type: "bar",
                    orientation: "h",
                    marker: {color: name === 'Community' ? '#0499fe' : '#e3e3e3'},
                    hovertemplate: "%{x:.2f}%",
                    name: name
                }
            },
            hplotlify(d) {
                return d ? [this._hplotlify(d.Community, "Community"),
                    this._hplotlify(d.Panel, "Panel")] : []
            },
            plotlify(d) {
                return d ? [this._plotlify(d.Community, "Community"),
                    this._plotlify(d.Panel, "Panel")] : []
            },
            deciles (d) {
                function trace(data, n) {
                    let color = n === "Community" ? '#0499fe':'#e3e3e3'
                    return { type: "scatter",
                             x: data,
                             y: Array(9).fill(n),
                             text: ["1st decile", "2nd decile", "3rd decile",
                                    "4th decile", "5th decile", "6th decile",
                                    "7th decile", "8th decile", "9th decile"],
                             hovertemplate: '%{text}<extra></extra>',
                             mode: 'markers',
                             marker: {
                                 color: color,
                                 symbol: 'circle',
                                 size: 16
                             }
                           }
                }
                return d ? [trace(d.Community, "Community"), trace(d.Panel, "Panel")] : []
            },

            round(x) {
                return Number.parseFloat(x).toFixed(1)
            },
            hintForCuriosity(c) {
                let cmax = _.maxBy(c.Community, v => v.y)
                if (cmax.x === "1-100") {
                    return `The majority of members (${this.round(cmax.y)}%) have between 1 and 100 followers.`
                } else if (cmax.x === "100-500") {
                    return `The majority of members (${this.round(cmax.y)}%) have between 100 and 500 followers.`
                } else if (cmax.x === "500-1000") {
                    return `The majority of members (${this.round(cmax.y)}%) have between 500 and 1000 followers.`
                } else {
                    return `The majority of members (${this.round(cmax.y)}%) have more than 1000+ followers.`
                }
            },
            hintProCuriosity(c) {
                let index0 = {Media: 0, Politics: 0, Journalists: 0}
                let community = _.reduce(c.Community,
                    (accum, e) => _.set(accum, e.x, e.y),
                    index0)
                let diffs = _.reduce(c.Panel,
                    (accum, e) => _.set(accum, e.x, e.y - _.get(accum, e.x)),
                    community)
                let descr = _.mapValues(diffs, function (v) {
                    if (v < 0) {
                        return "more"
                    } else if (v == 0) {
                        return "eqully"
                    } else {
                        return "less"
                    }
                })

                return `Members of the community are
                ${descr.Media} connected to Lucy Media,
                ${descr.Politics} connected to Lucy Politics,
                ${descr.Journalists} connected to Lucy Journalists,`
            },
            hintPodium(podium) {
                let accounts = _.map(podium.data.Community, e => e.x)
                if (podium.data.type === 'followers') {
                    return accounts.join(", ") + ` are the most followed accounts from ${podium.name} among members of the community`
                } else if (podium.data.type === 'topics') {
                    return accounts.join(", ") + ` are the most discussed social topics among members of the community`
                } else if (podium.data.type === 'all-followers') {
                    return accounts.join(", ") + ` are the most followed accounts among members of the community`
                } else if (podium.data.type === 'followers-other') {
                    return accounts.join(", ") + ` are the most followed accounts other from podium communities among members of the community`
                } else {
                    return ""
                }
            },
            podiumSubtitle(podium) {
                if (podium.data.type === 'followers') {
                    return `% of members following accounts from ${podium.name}`
                } else if (podium.data.type === 'topics') {
                    return "% of members tweeting about social topic"
                } else {
                    return ""
                }
            },
            fetchInfluenceKPIs() {
              var cs = new CommunityService();
              return cs.getInfluenceKPIs(
                  {
                      startDate: this.project.timeframe_start,
                      endDate: this.project.timeframe_end,
                      query: this.community
                  },
                  null
              ).then(resp => {
                  // Only 200 gives real data
                  if (resp.status == 200) {
                    this.kpis = resp.data
                  }
                  return resp;
              })
            },
            orderPodiums(podiums) {
                // Take a list of podiums and return those in expected order on page
                var local_podiums = [...podiums];
                var expected_order = [
                  "Media",
                  "Journalists",
                  "TV Shows",
                  "International Organizations",
                  "State Organizations",
                  "International Politicians",
                  "National Politicians",
                  "Regional Politicians",
                  "Brands",
                  "Youtubers",
                  "Influencers",
                  "Most followed accounts",
                  "Social topics"
                ];

                return local_podiums.sort(function(a, b) {
                  var ia = expected_order.indexOf(a.name);
                  var ib = expected_order.indexOf(b.name);
                  if (ia < ib) {
                    return -1;
                  }
                  if (ia > ib) {
                    return 1;
                  }
                  return 0;
                });
            },
            proCuriosityAdapter(proCuriosityIndex) {
              // Adjust labels of proCuriosity to more descriptive ones #573
              var labelRenamer = function(dp) {
                  return {
                    ...dp,
                    x: "More than 3 <br>" + dp.x.toLowerCase() + " ",
                  }
              };
              return {
                Community: _.map(proCuriosityIndex.Community, labelRenamer),
                Panel:  _.map(proCuriosityIndex.Panel, labelRenamer),
              }
            }
        },
    }
</script>

<style scoped>
    .inner-text {
        height: 100%;
        width: 100%;
        margin-top: 85px

    }

    .inner-text span {
        max-width: 100px;
        color: red;
        display: flex;
        align-items: center;
        justify-content: center
    }

    .kpi-value {
        align-items: center;
        justify-content: center;
        text-align: center
    }
</style>
